import { select as d3Select } from 'd3-selection'
import { interpolate as d3Interpolate } from 'd3-interpolate';
import * as d3ease from 'd3-ease';

import { useLayoutEffect } from 'react';
// Tools
import { no_propagate } from './utils';
// Custom hooks
import useAnimated from './useAnimated';
// Constants
import * as constants from './constants';

const Label = ({ person, radius, radius_off_screen, num_person_trips, selected_person_change, initial_animation_delay, set_focused_this_person, set_unhighlighted_person, set_selected_this_person }) => {

  const font_size = Math.min(Math.max(radius * 8 / num_person_trips, constants.MIN_LABEL_FONT_SIZE), constants.MAX_LABEL_FONT_SIZE);
  const angle = (person.start_angle + person.end_angle) / 2;
  const align_right = angle > Math.PI;
  let label_text = person.name;
  if (person.group_size > 1) {
    label_text += ` (${person.group_size})`;
  }

  // A generator for the transform attribute, which is a function
  // of angle and radius. We use this generator in useAnimated below.
  const transform_generator = ({angle, translate}) => `
                          rotate(${angle * 180 / Math.PI - 90})
                          translate(${translate})
                          ${angle > Math.PI ? "rotate(180)" : ""}
                        `;
  // A generator for the text-anchor attribute, which is a function of angle.
  const text_anchor_generator = ({angle}) => angle > Math.PI ? "end" : "start";
  // dx is already a number, but going through a generator puts it in the same
  // param space as the other two, which is what lets useAnimated measure how
  // fast it is moving when the slide-in below gets interrupted.
  const dx_generator = ({dx}) => dx;
  // Shared by both shape-specs below so 'transform' and 'text-anchor'
  // interpolate the exact same angle sequence at every tick of the one shared
  // transition, and so flip on the same tick as each other.
  const shared_params = {angle, translate : radius + 5};

  const [anim_ref, {transform, 'text-anchor' : text_anchor, dx}, record_params] = useAnimated({
    attrs : {
      'transform'   : {generator : transform_generator,   params : shared_params},
      'text-anchor' : {generator : text_anchor_generator, params : shared_params},
      // dx is the slide-in offset used by the initial animation below. It rests
      // at 0, so it is a no-op for ordinary reorders, but handing it to
      // useAnimated means a reorder that interrupts the slide-in eases dx the
      // rest of the way home rather than snapping it there.
      'dx'          : {generator : dx_generator, params : {dx : 0}}
    },
    duration : selected_person_change ? constants.SELECTION_ANIM_DURATION : constants.REORDER_ANIM_DURATION,
    deps : [angle]
  });

  // After initial render (but never again) slide the label in from off screen
  // by animating dx back to its resting value of 0. Report each tick so that a
  // reorder interrupting the slide picks it up mid-flight, at the speed it was
  // already travelling.
  useLayoutEffect(() => {
    const element = d3Select(anim_ref.current);
    const init_dx_delta = radius_off_screen - radius + constants.NODE_THICKNESS;
    const init_params = {dx : align_right ? -init_dx_delta : init_dx_delta};
    const interpolator = d3Interpolate(init_params, {dx : 0});
    // Record synchronously too - there is a delay before the first tick, and an
    // interruption during it would otherwise find no recorded position.
    record_params('dx', init_params);
    element
      .attr('dx', dx_generator(init_params))
      .transition()
      .duration(constants.NODELABEL_INITIAL_ANIM_DURATION)
      .delay(initial_animation_delay * constants.NODELABEL_INITIAL_ANIM_DELAY_RANGE)
      .ease(d3ease.easeBackOut)
      .attrTween('dx', () => (t) => {
        const params = interpolator(t);
        record_params('dx', params);
        return dx_generator(params);
      });

    return () => element.interrupt();
  }, []);

  return (
    <text key={`text_${person.person_index}`}
          className="person_label"
          ref={anim_ref}
          data-person_index={person.person_index}
          fill="white"
          dy="0.35em"
          dx={dx}
          style={{fontSize : font_size}}
          transform={transform}
          textAnchor={text_anchor}
          onMouseEnter={set_focused_this_person}
          onMouseLeave={set_unhighlighted_person}
          onClick={no_propagate(set_selected_this_person)}>
      {label_text}
    </text>
  );
};

export default Label;
